import React, { useState, useRef } from 'react';
import {
  DocumentTextIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';

interface KeyFinding {
  finding: string;
  significance: string;
  severity: 'normal' | 'mild' | 'moderate' | 'significant' | 'urgent';
}

interface Section {
  title: string;
  original_text: string;
  interpretation: string;
}

interface TermExplanation {
  term: string;
  explanation: string;
}

interface ScoringSystem {
  system: string;
  full_name: string;
  value: string;
  interpretation: string;
}

interface AnatomyResource {
  title: string;
  url: string;
  source: string;
  description: string;
}

interface InterpretationResult {
  imaging_type: string;
  imaging_name: string;
  imaging_subtype: string;
  summary: string;
  sections: Section[];
  key_findings: KeyFinding[];
  scoring_info: {
    detected_systems: ScoringSystem[];
    note: string;
  } | null;
  terminology_explained: TermExplanation[];
  follow_up_recommendations: string[];
  questions_for_doctor: string[];
  disclaimer: string;
  body_part_scanned?: string;
  anatomy_resources?: AnatomyResource[];
}

interface ImagingType {
  id: string;
  name: string;
  description: string;
  subtypes: string[];
}

export const ImagingInterpreter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [reportText, setReportText] = useState('');
  const [selectedImagingType, setSelectedImagingType] = useState<string>('');
  const [simpleMode, setSimpleMode] = useState(false);
  const [isInterpreting, setIsInterpreting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<InterpretationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [selectedTermForExplanation, setSelectedTermForExplanation] = useState<string | null>(null);
  const [termExplanation, setTermExplanation] = useState<any>(null);
  const [isExplainingTerm, setIsExplainingTerm] = useState(false);

  const imagingTypes: ImagingType[] = [
    { id: 'xray', name: t('imaging:types.xray', 'X-Ray'), description: 'Standard radiograph imaging', subtypes: [] },
    { id: 'ct', name: t('imaging:types.ct', 'CT Scan'), description: 'Computed Tomography', subtypes: [] },
    { id: 'mri', name: t('imaging:types.mri', 'MRI'), description: 'Magnetic Resonance Imaging', subtypes: [] },
    { id: 'ultrasound', name: t('imaging:types.ultrasound', 'Ultrasound'), description: 'Sonographic imaging', subtypes: [] },
    { id: 'echocardiogram', name: t('imaging:types.echocardiogram', 'Echocardiogram'), description: 'Heart ultrasound imaging', subtypes: [] },
    { id: 'mammogram', name: t('imaging:types.mammogram', 'Mammogram'), description: 'Breast imaging', subtypes: [] },
    { id: 'dexa', name: t('imaging:types.dexa', 'DEXA Scan'), description: 'Bone density scan', subtypes: [] },
    { id: 'pet', name: t('imaging:types.pet', 'PET Scan'), description: 'Positron Emission Tomography', subtypes: [] },
    { id: 'nuclear', name: t('imaging:types.nuclear', 'Nuclear Medicine'), description: 'Nuclear imaging studies', subtypes: [] },
    { id: '', name: t('imaging:types.auto', 'Auto-detect'), description: 'Let AI determine the type', subtypes: [] },
  ];

  const handleInterpret = async () => {
    if (!reportText.trim()) {
      setError(t('imaging:errors.noText', 'Please paste or upload a report to interpret.'));
      return;
    }

    setIsInterpreting(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiClient.post('/imaging/interpret', {
        report_text: reportText,
        imaging_type: selectedImagingType || undefined,
        simple_explanation: simpleMode,
        language: i18n.language,
      });
      setResult(response.data);
      // Expand summary section by default
      setExpandedSections({ summary: true, findings: true });
    } catch (err: any) {
      console.error('Interpretation failed:', err);
      setError(err.response?.data?.detail || t('imaging:errors.interpretFailed', 'Failed to interpret the report. Please try again.'));
    } finally {
      setIsInterpreting(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['.pdf', '.docx', '.txt'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));

    if (!allowedTypes.includes(fileExt)) {
      setError(t('imaging:errors.unsupportedFile', 'Please upload a PDF, DOCX, or TXT file.'));
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (selectedImagingType) {
        formData.append('imaging_type', selectedImagingType);
      }
      formData.append('simple_explanation', String(simpleMode));
      formData.append('language', i18n.language);

      const response = await apiClient.post('/imaging/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data);
      setExpandedSections({ summary: true, findings: true });
      // Also set the report text for reference
      setReportText(`[Uploaded file: ${file.name}]`);
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail || t('imaging:errors.uploadFailed', 'Failed to process the uploaded file. Please try pasting the text instead.'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExplainTerm = async (term: string) => {
    setSelectedTermForExplanation(term);
    setIsExplainingTerm(true);

    try {
      const response = await apiClient.post('/imaging/explain-term', {
        term,
        simple_explanation: simpleMode,
        language: i18n.language,
      });
      setTermExplanation(response.data);
    } catch (err) {
      console.error('Failed to explain term:', err);
      setTermExplanation({
        term,
        simple_definition: t('imaging:errors.termExplainFailed', 'Unable to explain this term. Please ask your healthcare provider.'),
      });
    } finally {
      setIsExplainingTerm(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'normal':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'mild':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'significant':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleReset = () => {
    setReportText('');
    setResult(null);
    setError(null);
    setSelectedImagingType('');
    setExpandedSections({});
    setTermExplanation(null);
    setSelectedTermForExplanation(null);
  };

  // Generate export content (markdown) — structure mirrors the on-screen UI
  const generateExportContent = (): string => {
    if (!result) return '';

    const lines: string[] = [];

    // Header / metadata
    lines.push(`**Imaging Type:** ${result.imaging_name} (${result.imaging_type})`);
    if (result.imaging_subtype) {
      lines.push(`**Subtype:** ${result.imaging_subtype}`);
    }
    if (result.body_part_scanned) {
      lines.push(`**Body Part Scanned:** ${result.body_part_scanned}`);
    }
    lines.push(`**Date:** ${new Date().toLocaleDateString()}`);
    lines.push('');

    // Summary
    lines.push('## Summary');
    lines.push('');
    lines.push(result.summary);
    lines.push('');

    // Scoring Systems
    if (result.scoring_info && result.scoring_info.detected_systems.length > 0) {
      lines.push('## Scoring Systems Detected');
      lines.push('');
      result.scoring_info.detected_systems.forEach((s) => {
        lines.push(`### ${s.system}`);
        lines.push('');
        lines.push(`**Full Name:** ${s.full_name}`);
        lines.push(`**Value:** ${s.value}`);
        lines.push('');
        lines.push(s.interpretation);
        lines.push('');
      });
      if (result.scoring_info.note) {
        lines.push(`*${result.scoring_info.note}*`);
        lines.push('');
      }
    }

    // Key Findings — color the finding heading and the severity label.
    // Significance text stays in default color so emphasis lives only on the
    // labels the user wants colored (matching the on-screen severity badge).
    const colorableSeverities = new Set(['normal', 'mild', 'moderate', 'significant', 'urgent']);
    const wrap = (severity: string | undefined, body: string): string => {
      const s = (severity || '').toLowerCase();
      return colorableSeverities.has(s) ? `[[c:${s}]]${body}[[/c]]` : body;
    };
    if (result.key_findings.length > 0) {
      lines.push('## Key Findings');
      lines.push('');
      result.key_findings.forEach((f) => {
        lines.push(`### ${wrap(f.severity, f.finding)}`);
        lines.push('');
        lines.push(wrap(f.severity, `**Severity:** ${f.severity.toUpperCase()}`));
        lines.push('');
        lines.push(f.significance);
        lines.push('');
      });
    }

    // Report Sections — original text in italics, interpretation in normal (mirrors UI)
    if (result.sections.length > 0) {
      lines.push('## Report Sections');
      lines.push('');
      result.sections.forEach((s) => {
        lines.push(`### ${s.title}`);
        lines.push('');
        if (s.original_text) {
          lines.push(`*Original: "${s.original_text}"*`);
          lines.push('');
        }
        lines.push(s.interpretation);
        lines.push('');
      });
    }

    // Medical Terms Explained — bold term, normal explanation
    if (result.terminology_explained && result.terminology_explained.length > 0) {
      lines.push('## Medical Terms Explained');
      lines.push('');
      result.terminology_explained.forEach((t) => {
        lines.push(`**${t.term}:** ${t.explanation}`);
        lines.push('');
      });
    }

    // Follow-up Recommendations
    if (result.follow_up_recommendations && result.follow_up_recommendations.length > 0) {
      lines.push('## Follow-up Recommendations');
      lines.push('');
      result.follow_up_recommendations.forEach((r) => lines.push(`- ${r}`));
      lines.push('');
    }

    // Questions for Doctor (numbered list, mirrors UI)
    if (result.questions_for_doctor.length > 0) {
      lines.push('## Questions to Ask Your Doctor');
      lines.push('');
      result.questions_for_doctor.forEach((q, i) => lines.push(`${i + 1}. ${q}`));
      lines.push('');
    }

    // Anatomy Resources — italic source, plain description, bold URL label
    if (result.anatomy_resources && result.anatomy_resources.length > 0) {
      const bodyPart = result.body_part_scanned || 'This Body Part';
      lines.push(`## Learn More About ${bodyPart}`);
      lines.push('');
      result.anatomy_resources.forEach((r) => {
        lines.push(`### ${r.title}`);
        lines.push('');
        lines.push(`*${r.source}*`);
        lines.push('');
        lines.push(r.description);
        lines.push('');
        lines.push(`**URL:** ${r.url}`);
        lines.push('');
      });
    }

    // Disclaimer
    lines.push('## Disclaimer');
    lines.push('');
    lines.push(result.disclaimer);
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push(`*Generated by Auxilio Health — ${new Date().toLocaleString()}*`);

    return lines.join('\n');
  };

  // Print imaging results
  const printResults = () => {
    if (!result) return;

    const date = new Date().toLocaleDateString();
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const escapeHtml = (s: string) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Imaging Report - ${escapeHtml(result.imaging_name)} - ${date}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
              line-height: 1.6;
              color: #1f2937;
              max-width: 800px;
              margin: 0 auto;
              padding: 40px 20px;
            }
            h1 { font-size: 24px; margin-bottom: 5px; color: #1f2937; }
            h2 { font-size: 18px; color: #374151; margin-top: 20px; margin-bottom: 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
            .subtitle { font-size: 14px; color: #6b7280; margin-bottom: 20px; }
            .type-badge { display: inline-block; background: #dbeafe; color: #1e40af; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; margin-right: 6px; }
            .meta-line { font-size: 13px; color: #4b5563; margin: 4px 0; }
            .summary { background: #f9fafb; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .finding { margin-bottom: 15px; padding: 12px; border: 1px solid #e5e7eb; border-radius: 8px; page-break-inside: avoid; }
            .finding-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
            .finding-text { font-weight: 600; color: #1f2937; }
            .severity { padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 500; }
            .severity-normal { background: #d1fae5; color: #065f46; }
            .severity-mild { background: #dbeafe; color: #1e40af; }
            .severity-moderate { background: #fef3c7; color: #92400e; }
            .severity-significant { background: #ffedd5; color: #c2410c; }
            .severity-urgent { background: #fee2e2; color: #b91c1c; }
            /* Text-only color classes for the finding name (matches the badge palette). */
            .text-normal { color: #065f46; }
            .text-mild { color: #1e40af; }
            .text-moderate { color: #92400e; }
            .text-significant { color: #c2410c; }
            .text-urgent { color: #b91c1c; }
            .scoring-item { margin-bottom: 12px; padding: 10px; background: #eff6ff; border-radius: 6px; page-break-inside: avoid; }
            .scoring-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
            .scoring-system { font-weight: 600; color: #1e3a8a; }
            .scoring-value { padding: 2px 8px; background: #bfdbfe; color: #1e3a8a; border-radius: 4px; font-size: 12px; font-weight: 500; }
            .scoring-fullname { font-size: 12px; color: #2563eb; }
            .scoring-interpretation { font-size: 13px; color: #4b5563; margin-top: 4px; }
            .scoring-note { font-size: 11px; color: #6b7280; font-style: italic; margin-top: 8px; }
            .section { margin-bottom: 15px; padding: 12px; background: #f9fafb; border-radius: 8px; page-break-inside: avoid; }
            .section-title { font-weight: 600; color: #374151; margin-bottom: 5px; }
            .section-original { font-size: 13px; color: #6b7280; font-style: italic; margin-bottom: 5px; }
            .section-interpretation { font-size: 14px; color: #1f2937; }
            .term-item { margin-bottom: 8px; padding: 8px 10px; background: #fffbeb; border-radius: 6px; }
            .term-name { font-weight: 600; color: #92400e; }
            .term-explanation { font-size: 13px; color: #4b5563; margin-top: 2px; }
            .list-item { margin: 8px 0; padding-left: 20px; position: relative; }
            .list-item:before { content: "•"; position: absolute; left: 5px; color: #10b981; }
            .resource-item { margin-bottom: 12px; padding: 10px; background: #faf5ff; border-radius: 6px; page-break-inside: avoid; }
            .resource-title { font-weight: 600; color: #581c87; }
            .resource-source { font-size: 11px; color: #6b7280; margin-bottom: 4px; }
            .resource-url { font-size: 11px; color: #2563eb; word-break: break-all; }
            .resource-description { font-size: 12px; color: #4b5563; margin-top: 4px; }
            .disclaimer { margin-top: 30px; padding: 15px; background: #fef3c7; border-radius: 8px; font-size: 12px; color: #92400e; }
            .footer { margin-top: 20px; text-align: center; font-size: 11px; color: #9ca3af; }
            @media print {
              body { padding: 20px; }
              .finding, .section, .scoring-item, .resource-item { border: 1px solid #ccc; }
              a { color: inherit; text-decoration: none; }
            }
          </style>
        </head>
        <body>
          <h1>Imaging Report Interpretation</h1>
          <p class="subtitle">Generated on ${date}</p>
          <span class="type-badge">${escapeHtml(result.imaging_name)}</span>
          ${result.imaging_subtype ? `<span class="type-badge">${escapeHtml(result.imaging_subtype)}</span>` : ''}
          ${result.body_part_scanned ? `<div class="meta-line"><strong>Body Part:</strong> ${escapeHtml(result.body_part_scanned)}</div>` : ''}

          <div class="summary">
            <h2>Summary</h2>
            <p>${escapeHtml(result.summary)}</p>
          </div>

          ${result.scoring_info && result.scoring_info.detected_systems.length > 0 ? `
            <h2>Scoring Systems Detected</h2>
            ${result.scoring_info.detected_systems.map(s => `
              <div class="scoring-item">
                <div class="scoring-header">
                  <span class="scoring-system">${escapeHtml(s.system)}</span>
                  <span class="scoring-value">${escapeHtml(s.value)}</span>
                </div>
                <div class="scoring-fullname">${escapeHtml(s.full_name)}</div>
                <div class="scoring-interpretation">${escapeHtml(s.interpretation)}</div>
              </div>
            `).join('')}
            ${result.scoring_info.note ? `<div class="scoring-note">${escapeHtml(result.scoring_info.note)}</div>` : ''}
          ` : ''}

          ${result.key_findings.length > 0 ? `
            <h2>Key Findings</h2>
            ${result.key_findings.map(f => {
              const sev = (f.severity || '').toLowerCase();
              const colorable = ['normal', 'mild', 'moderate', 'significant', 'urgent'].includes(sev);
              const textCls = colorable ? `text-${sev}` : '';
              return `
              <div class="finding">
                <div class="finding-header">
                  <span class="finding-text ${textCls}">${escapeHtml(f.finding)}</span>
                  <span class="severity severity-${escapeHtml(f.severity)}">${escapeHtml(f.severity.toUpperCase())}</span>
                </div>
                <p style="margin: 5px 0; font-size: 13px; color: #4b5563;">${escapeHtml(f.significance)}</p>
              </div>
              `;
            }).join('')}
          ` : ''}

          ${result.sections.length > 0 ? `
            <h2>Report Sections</h2>
            ${result.sections.map(s => `
              <div class="section">
                <div class="section-title">${escapeHtml(s.title)}</div>
                ${s.original_text ? `<div class="section-original">Original: "${escapeHtml(s.original_text)}"</div>` : ''}
                <div class="section-interpretation">${escapeHtml(s.interpretation)}</div>
              </div>
            `).join('')}
          ` : ''}

          ${result.terminology_explained && result.terminology_explained.length > 0 ? `
            <h2>Medical Terms Explained</h2>
            ${result.terminology_explained.map(t => `
              <div class="term-item">
                <div class="term-name">${escapeHtml(t.term)}</div>
                <div class="term-explanation">${escapeHtml(t.explanation)}</div>
              </div>
            `).join('')}
          ` : ''}

          ${result.follow_up_recommendations && result.follow_up_recommendations.length > 0 ? `
            <h2>Follow-up Recommendations</h2>
            ${result.follow_up_recommendations.map(r => `<div class="list-item">${escapeHtml(r)}</div>`).join('')}
          ` : ''}

          ${result.questions_for_doctor.length > 0 ? `
            <h2>Questions for Your Doctor</h2>
            ${result.questions_for_doctor.map(q => `<div class="list-item">${escapeHtml(q)}</div>`).join('')}
          ` : ''}

          ${result.anatomy_resources && result.anatomy_resources.length > 0 ? `
            <h2>Learn More About ${escapeHtml(result.body_part_scanned || 'This Body Part')}</h2>
            ${result.anatomy_resources.map(r => `
              <div class="resource-item">
                <div class="resource-title">${escapeHtml(r.title)}</div>
                <div class="resource-source">${escapeHtml(r.source)}</div>
                <div class="resource-url">${escapeHtml(r.url)}</div>
                <div class="resource-description">${escapeHtml(r.description)}</div>
              </div>
            `).join('')}
          ` : ''}

          <div class="disclaimer">
            <strong>Disclaimer:</strong> ${escapeHtml(result.disclaimer)}
          </div>

          <div class="footer">
            Generated by Auxilio Health • ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Handle export
  const handleExport = async (format: 'word' | 'pdf' | 'text') => {
    if (!result) return;

    const content = generateExportContent();
    const title = `Imaging Report - ${result.imaging_name}`;

    const formatMap: Record<string, string> = {
      'word': 'docx',
      'pdf': 'pdf',
      'text': 'txt',
    };

    try {
      const response = await apiClient.post('/export/document', {
        content,
        title,
        format: formatMap[format]
      }, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.${formatMap[format]}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Loading Overlay for file uploads */}
      <LoadingOverlay
        isVisible={isUploading}
        message={t('imaging:loading.uploading', 'Processing...')}
        subMessage={t('imaging:loading.pleaseWait', 'Analyzing your imaging report')}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <DocumentTextIcon className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('imaging:title', 'Medical Imaging Report Interpreter')}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          {t('imaging:subtitle', 'Upload or paste your imaging report to get a plain-language explanation')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="space-y-4">
          {/* Imaging Type Selection */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('imaging:imagingType', 'Imaging Type')}
            </label>
            <select
              value={selectedImagingType}
              onChange={(e) => setSelectedImagingType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              {imagingTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} - {type.description}
                </option>
              ))}
            </select>
          </div>

          {/* Simple Mode Toggle */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={simpleMode}
                onChange={(e) => setSimpleMode(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('imaging:simpleMode', 'Simple explanation mode')}
              </span>
            </label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('imaging:simpleModeDesc', 'Use everyday language, as if explaining to someone with no medical background')}
            </p>
          </div>

          {/* Report Input */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('imaging:reportText', 'Report Text')}
            </label>
            <textarea
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              placeholder={t('imaging:reportPlaceholder', 'Paste your imaging report here...')}
              className="w-full h-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
            />

            {/* File Upload */}
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg cursor-pointer transition-colors"
                >
                  {isUploading ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowUpTrayIcon className="h-4 w-4" />
                  )}
                  <span>{isUploading ? t('imaging:uploading', 'Uploading...') : t('imaging:uploadFile', 'Upload PDF/DOCX/TXT')}</span>
                </label>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('imaging:charCount', '{{count}} characters', { count: reportText.length })}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleInterpret}
              disabled={isInterpreting || !reportText.trim()}
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium rounded-lg transition-colors"
            >
              {isInterpreting ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  <span>{t('imaging:interpreting', 'Interpreting...')}</span>
                </>
              ) : (
                <>
                  <SparklesIcon className="h-5 w-5" />
                  <span>{t('imaging:interpret', 'Interpret Report')}</span>
                </>
              )}
            </button>
            {result && (
              <button
                onClick={handleReset}
                className="px-4 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
              >
                {t('imaging:reset', 'Reset')}
              </button>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-4">
          {!result && !isInterpreting && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <DocumentTextIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('imaging:noResultYet', 'Paste or upload your imaging report and click "Interpret Report" to see the results.')}
              </p>
            </div>
          )}

          {isInterpreting && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
              <ArrowPathIcon className="h-12 w-12 mx-auto mb-4 text-emerald-500 animate-spin" />
              <p className="text-gray-600 dark:text-gray-400">
                {t('imaging:analyzingReport', 'Analyzing your imaging report...')}
              </p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {/* Imaging Type Badge with Export/Print buttons */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {t('imaging:detectedType', 'Detected Type')}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {result.imaging_subtype || result.imaging_name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm font-medium rounded-full">
                      {result.imaging_name}
                    </span>
                    <div className="border-l border-gray-300 dark:border-gray-600 h-6 mx-1"></div>
                    {/* Export/Print Dropdown */}
                    <ExportDropdownMenu
                      onExport={handleExport}
                      onPrint={printResults}
                      showPrint={true}
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <button
                  onClick={() => toggleSection('summary')}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                    <InformationCircleIcon className="h-5 w-5 text-emerald-600" />
                    <span>{t('imaging:summary', 'Summary')}</span>
                  </h3>
                  {expandedSections['summary'] ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                {expandedSections['summary'] && (
                  <p className="mt-3 text-gray-700 dark:text-gray-300">{result.summary}</p>
                )}
              </div>

              {/* Scoring Systems */}
              {result.scoring_info && result.scoring_info.detected_systems.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <button
                    onClick={() => toggleSection('scoring')}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                      <span>{t('imaging:scoringSystems', 'Scoring Systems Detected')}</span>
                    </h3>
                    {expandedSections['scoring'] ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections['scoring'] && (
                    <div className="mt-3 space-y-3">
                      {result.scoring_info.detected_systems.map((score, index) => (
                        <div key={index} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-blue-800 dark:text-blue-300">{score.system}</span>
                            <span className="px-2 py-0.5 bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 text-sm rounded">
                              {score.value}
                            </span>
                          </div>
                          <p className="text-sm text-blue-700 dark:text-blue-400">{score.full_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{score.interpretation}</p>
                        </div>
                      ))}
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">{result.scoring_info.note}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Key Findings */}
              {result.key_findings.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <button
                    onClick={() => toggleSection('findings')}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                      <ClipboardDocumentIcon className="h-5 w-5 text-purple-600" />
                      <span>{t('imaging:keyFindings', 'Key Findings')}</span>
                    </h3>
                    {expandedSections['findings'] ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections['findings'] && (
                    <div className="mt-3 space-y-3">
                      {result.key_findings.map((finding, index) => {
                        // Severity-driven text color so finding name visually matches its badge.
                        const sevTextColor =
                          finding.severity === 'normal'      ? 'text-green-700 dark:text-green-400' :
                          finding.severity === 'mild'        ? 'text-blue-700 dark:text-blue-400' :
                          finding.severity === 'moderate'    ? 'text-yellow-700 dark:text-yellow-400' :
                          finding.severity === 'significant' ? 'text-orange-700 dark:text-orange-400' :
                          finding.severity === 'urgent'      ? 'text-red-700 dark:text-red-400' :
                                                                'text-gray-900 dark:text-white';
                        return (
                          <div key={index} className="border-l-4 border-gray-200 dark:border-gray-600 pl-3 py-2">
                            <div className="flex items-start justify-between">
                              <p className={`font-medium ${sevTextColor}`}>{finding.finding}</p>
                              <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded ${getSeverityColor(finding.severity)}`}>
                                {t(`imaging:severity.${finding.severity}`, finding.severity)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{finding.significance}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Report Sections */}
              {result.sections.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <button
                    onClick={() => toggleSection('sections')}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                      <DocumentTextIcon className="h-5 w-5 text-gray-600" />
                      <span>{t('imaging:reportSections', 'Report Sections')}</span>
                    </h3>
                    {expandedSections['sections'] ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections['sections'] && (
                    <div className="mt-3 space-y-4">
                      {result.sections.map((section, index) => (
                        <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                          <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">{section.title}</h4>
                          {section.original_text && (
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-2 mb-2 text-sm text-gray-600 dark:text-gray-400 italic">
                              "{section.original_text}"
                            </div>
                          )}
                          <p className="text-sm text-gray-700 dark:text-gray-300">{section.interpretation}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Terminology */}
              {result.terminology_explained.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <button
                    onClick={() => toggleSection('terminology')}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                      <QuestionMarkCircleIcon className="h-5 w-5 text-amber-600" />
                      <span>{t('imaging:terminology', 'Medical Terms Explained')}</span>
                    </h3>
                    {expandedSections['terminology'] ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections['terminology'] && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {result.terminology_explained.map((item, index) => (
                        <button
                          key={index}
                          onClick={() => handleExplainTerm(item.term)}
                          className="text-left p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors"
                        >
                          <span className="font-medium text-amber-800 dark:text-amber-300">{item.term}</span>
                          <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{item.explanation}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Follow-up Recommendations */}
              {result.follow_up_recommendations.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    <span>{t('imaging:followUp', 'Follow-up Recommendations')}</span>
                  </h3>
                  <ul className="space-y-2">
                    {result.follow_up_recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-green-500 mt-1">•</span>
                        <span className="text-gray-700 dark:text-gray-300">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Questions for Doctor */}
              {result.questions_for_doctor.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                    <QuestionMarkCircleIcon className="h-5 w-5 text-indigo-600" />
                    <span>{t('imaging:questionsForDoctor', 'Questions to Ask Your Doctor')}</span>
                  </h3>
                  <ul className="space-y-2">
                    {result.questions_for_doctor.map((question, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="text-indigo-500 mt-1">{index + 1}.</span>
                        <span className="text-gray-700 dark:text-gray-300">{question}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Anatomy Resources */}
              {result.anatomy_resources && result.anatomy_resources.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                    <LinkIcon className="h-5 w-5 text-purple-600" />
                    <span>
                      {t('imaging:anatomyResources', 'Learn More About')} {result.body_part_scanned || t('imaging:thisBodyPart', 'This Body Part')}
                    </span>
                  </h3>
                  <div className="space-y-3">
                    {result.anatomy_resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-purple-800 dark:text-purple-300">{resource.title}</h4>
                            <p className="text-xs text-purple-600 dark:text-purple-400 mb-1">{resource.source}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{resource.description}</p>
                          </div>
                          <LinkIcon className="h-4 w-4 text-purple-500 flex-shrink-0 ml-2 mt-1" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">{result.disclaimer}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Term Explanation Modal */}
      {selectedTermForExplanation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{selectedTermForExplanation}</h3>
              <button
                onClick={() => {
                  setSelectedTermForExplanation(null);
                  setTermExplanation(null);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            {isExplainingTerm ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="h-8 w-8 text-emerald-500 animate-spin" />
              </div>
            ) : termExplanation ? (
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('imaging:termModal.definition', 'Definition')}
                  </h4>
                  <p className="text-gray-700 dark:text-gray-300">{termExplanation.simple_definition}</p>
                </div>
                {termExplanation.detailed_explanation && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('imaging:termModal.details', 'More Details')}
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">{termExplanation.detailed_explanation}</p>
                  </div>
                )}
                {termExplanation.why_it_matters && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('imaging:termModal.whyItMatters', 'Why It Matters')}
                    </h4>
                    <p className="text-gray-700 dark:text-gray-300">{termExplanation.why_it_matters}</p>
                  </div>
                )}
                {termExplanation.related_terms && termExplanation.related_terms.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                      {t('imaging:termModal.relatedTerms', 'Related Terms')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {termExplanation.related_terms.map((term: string, index: number) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

    </div>
  );
};

export default ImagingInterpreter;
